import { FC, ReactNode, useEffect, useRef } from 'react';
import { Alert, AlertTitle, Box, Stack, StackProps } from '@mui/material';

import {
  AtsValidationErrorMessage,
  AtsValidationErrors as TValidationErrors,
} from '@models/ValidationError';

type ValidationErrorsProps = StackProps & {
  action?: ReactNode;
  messageSuffix?: ReactNode;
  validationErrors?: AtsValidationErrorMessage | TValidationErrors;
};

export const ValidationErrors: FC<ValidationErrorsProps> = ({
  action,
  messageSuffix,
  validationErrors,
  spacing = 2,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (validationErrors) {
      window.scrollTo({ top: 0 });
    }
  }, [validationErrors]);

  if (!validationErrors) return null;

  if (typeof validationErrors === 'string')
    return (
      <Alert action={action} ref={ref} severity='error'>
        {validationErrors}
        {messageSuffix && <Box mt={1}>{messageSuffix}</Box>}
      </Alert>
    );

  if ('Message' in validationErrors && typeof validationErrors.Message === 'string')
    return (
      <Alert action={action} ref={ref} severity='error'>
        <AlertTitle>{validationErrors.Message}</AlertTitle>
        {validationErrors.Description}
        {messageSuffix && <Box mt={1}>{messageSuffix}</Box>}
      </Alert>
    );

  let errors;

  if (Array.isArray(validationErrors)) errors = validationErrors;
  else if (Array.isArray(validationErrors.Message)) errors = validationErrors.Message;

  if (errors)
    return errors.length === 0 ? null : (
      <Stack ref={ref} spacing={spacing} {...rest}>
        {errors.map(error =>
          error.Children?.length ? (
            <ValidationErrors
              action={action}
              key={error.Field + error.Message}
              messageSuffix={messageSuffix}
              validationErrors={error.Children}
            />
          ) : (
            <Alert action={action} key={error.Field + error.Message} severity='error'>
              {error.Message}
              {messageSuffix && <Box mt={1}>{messageSuffix}</Box>}
            </Alert>
          )
        )}
      </Stack>
    );
};
